<template>
  <div class="lg:container lg:mx-auto overflow-hidden">
    <Head :title="project.title" />
    <div class="flex flex-row justify-around">
      <div class="flex-auto pr-4 items-center">
        <span class="text-sm">Project</span>
        <h1 class="mb-8 text-4xl font-bold">{{ project.title }}</h1>
      </div>
      <div class="flex-auto flex items-center justify-end">
        <share
          :model-value="form.userSearch"
          :project="project"
          :users="users"
          :company="company"
          :permissions="permissions"
          @update:model-value="form.userSearch = $event"
          @reset="form.userSearch = null"
        />
      </div>
    </div>
    <!-- Title -->

    <div class="md:w-3/4 md:flex md:mb-4">
      <div class="md:w-3/4">
        <div class="flex items-center mb-2 max-w-sm">
          <p class="font-bold text-black md:w-2/5 lg:w-1/3">Project Address</p>
          <p class="text-gray-400">{{ project.address }}</p>
        </div>
        <div class="flex items-center mb-2 max-w-sm">
          <p class="font-bold text-black md:w-2/5 lg:w-1/3">City</p>
          <p class="text-gray-400">{{ project.city }}</p>
        </div>
        <div class="flex items-center mb-2 max-w-sm">
          <p class="font-bold text-black md:w-2/5 lg:w-1/3">State/Region</p>
          <p class="text-gray-400">{{ project.state }}</p>
        </div>
        <div class="flex items-center mb-2 max-w-sm">
          <p class="font-bold text-black md:w-2/5 lg:w-1/3">Country</p>
          <p class="text-gray-400">{{ project.country }}</p>
        </div>
        <div class="flex items-center mb-2 max-w-sm">
          <p class="font-bold text-black md:w-2/5 lg:w-1/3">Zip/Post code</p>
          <p class="text-gray-400">{{ project.zipcode }}</p>
        </div>
        <div v-if="auth.user.is_admin" class="flex items-center mb-2 max-w-sm">
          <p class="font-bold text-black md:w-2/5 lg:w-1/3">Status</p>
          <select-input :model-value="project.status" class="p-0" @update:model-value="updateStatus($event)">
            <option v-for="(v,k) in statuses" :key="k" :value="k"> {{ v }}</option>
          </select-input>
        </div>
      </div>
      <div class="md:w-2/4">
        <div class="flex items-center mb-2 max-w-sm">
          <p class="font-bold text-black md:w-2/5 lg:w-1/3">Created:</p>
          <p class="text-gray-400">{{ project.created_at }}</p>
        </div>
        <div class="flex items-center mb-2 max-w-sm">
          <p class="font-bold text-black md:w-2/5 lg:w-1/3">Last Edit:</p>
          <p class="text-gray-400">{{ project.updated_at }}</p>
        </div>
        <div class="flex items-center mb-2 max-w-sm">
          <p class="font-bold text-black md:w-2/5 lg:w-1/3">Total length:</p>
          <p class="text-gray-400">{{ project.length }} (m)</p>
        </div>
        <div class="flex items-center mb-2 max-w-sm">
          <p class="font-bold text-black md:w-2/5 lg:w-1/3">Total Parts:</p>
          <p class="text-gray-400">{{ project.parts }}</p>
        </div>
        <div class="flex items-center mb-2 max-w-sm">
          <p class="font-bold text-black md:w-2/5 lg:w-1/3">Total Weight:</p>
          <p class="text-gray-400">{{ project.weight }}</p>
        </div>
      </div>
    </div>
    <!-- Project details -->
    <div class="border-solid border-t border-blue-800 my-2"> &nbsp;</div>

    <div class="flex items-center justify-end flex-wrap">
      <div class="mt-2 md:mt-0 flex-none w-full max-w-max">
        <div class="md:flex">
          <h1 class="text-xl font-bold mr-2 mb-4 md:mb-0">Project Runs</h1>
          <div class="md:flex items-center mb-4 md:mb-0">
            <div class="flex items-center justify-between mr-2">
              <search-filter v-model="form.search" class="mr-4 w-full" @reset="reset">
                <label class="block text-gray-700">Deleted:</label>
                <select v-model="form.trashed" class="form-select mt-1 w-full">
                  <option :value="null">Exclude Deleted</option>
                  <option value="with">Include Deleted</option>
                  <option value="only">Only Deleted</option>
                </select>
              </search-filter>
            </div>
          </div>
          <div class="flex items-center mb-4 md:mb-0 flex-wrap">
            <Link v-if="project.permissions.run.create && project.permissions.run.custom" class="btn-blue h-10 bg-white border-2 border-solid border-blue-600 mr-4 text-blue-800" :href="`/projects/${project.id}/customs/create`">
              <span> + New Custom Run</span>
            </Link>
            <Link v-if="project.permissions.run.create" class="btn-blue h-10" :href="`/projects/${project.id}/runs/create`">
              <span> + New Run</span>
            </Link>
          </div>
        </div>
      </div>
      <div class="flex-none w-full">
        <ul v-if="selected.length > 0 && runs.length" class="text-center md:flex md:items-center">
          <li class="m-1">
            <div v-if="runs[0].permissions.update && form.trashed !=='only'" class="flex flex-row items-center">
              <select id="discount-multiplier" v-model="discount" name="discount-multiplier" class="form-select w-full" @change="bulkDiscount()">
                <option :value="null" selected>Discount Multiplier</option>
                <option v-for="d in company.discounts" :key="d.id" :value="d.id">{{ d.value }}</option>
              </select>
              <button class="btn-blue bg-gray-100 text-gray-800 border border-gray-400 cursor-pointer" @click="discount = null, bulkDiscount()">X</button>
            </div>
          </li>
          <li class="m-1">
            <download-bulk-button v-if="runs[0].permissions.view && form.trashed !=='only'" :project="project.id" :runs="selected" :label="true" class="w-full" />
          </li>
          <li class="m-1">
            <a v-if="runs[0].permissions.create && form.trashed !=='only'" class="block md:inline btn-blue bg-gray-100 text-gray-800 border border-gray-400 cursor-pointer" @click="bulkClone()">Duplicate</a>
          </li>
          <li class="m-1">
            <a v-if="runs[0].permissions.delete && form.trashed !=='only'" class="block md:inline btn-blue bg-gray-100 text-gray-800 border border-gray-400 cursor-pointer" @click="bulkDestroy()">Delete</a>
          </li>
          <li class="m-1">
            <a v-if="runs[0].permissions.restore && form.trashed === 'only'" class="block md:inline btn-blue bg-gray-100 text-gray-800 border border-gray-400 cursor-pointer" @click="bulkRestore()">Restore</a>
          </li>
        </ul>
      </div>
    </div>
    <!-- Bulk Actions -->

    <div class="overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <thead>
          <tr class="text-left font-bold text-lg">
            <th class="pt-2 px-4 text-center">
              <input v-show="runs.length" type="checkbox" :checked="isSelected" @click="toggle" />
            </th>
            <th class="pt-2 pr-4">System</th>
            <th class="pt-2 pr-4">Run Name</th>
            <th class="pt-2 px-4 text-center">Feet</th>
            <th class="pt-2 px-4 text-center">Meters</th>
            <th class="pt-2 px-4 text-center">Discount Multiplier</th>
            <th class="pt-2 text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(run, index) in runs" :key="run.id"
            class="hover:bg-gray-100 focus-within:bg-gray-100"
            :class="index % 2 == 0 ? '' : 'bg-gray-100'"
          >
            <td class="text-center">
              <input v-model="selected" type="checkbox" :value="run.id" />
            </td>
            <td>
              <Link class="flex items-center p-2 focus:text-blue-500" :href="`/runs/${run.id}/edit`" title="View/Edit Drawing">
                {{ run.system }}
                <icon v-if="run.deleted_at" name="trash" class="flex-shrink-0 ml-2 w-3 h-3 fill-gray-400" />
              </Link>
            </td>
            <td>
              <Link class="flex items-center p-2 focus:text-blue-500" :href="`/runs/${run.id}/edit`" title="View/Edit Drawing">
                {{ run.name }}
                <icon v-if="run.deleted_at" name="trash" class="flex-shrink-0 ml-2 w-3 h-3 fill-gray-400" />
              </Link>
            </td>
            <td class="text-center">
              {{ run.feet }}
            </td>
            <td class="text-center">
              {{ run.length }}
            </td>
            <td class="text-center">
              {{ run.discount }}
            </td>
            <td class="flex items-center">
              <download-button v-if="! run.deleted_at && run.permissions.view" :run-id="run.id" />
              <Link v-if="! run.deleted_at && run.permissions.view" class="p-2" :href="`/runs/${run.id}`" title="View Run Details">
                <icon name="eye" class="block w-4 h-4" />
              </Link>
              <Link v-if="! run.deleted_at && run.permissions.update" class="p-2" :href="`/runs/${run.id}/edit`" tabindex="-1" title="View/Edit Drawing">
                <icon name="edit" class="block w-4 h-4" />
              </Link>
              <button v-if="! run.deleted_at && run.permissions.create" class="p-2" type="button" title="Clone Run" @click="clone(run.id)">
                <icon name="clone" class="block w-4 h-4" />
              </button>
              <button v-if="! run.deleted_at && run.permissions.delete" class="p-2" type="button" title="Delete Run" @click="destroy(run.id)">
                <icon name="trash" class="block w-4 h-4" />
              </button>
              <button v-if="run.deleted_at && run.permissions.restore" class="p-2" type="button" title="Restore Run" @click="restore(run.id)">
                <icon name="trash" class="block w-4 h-4" />
              </button>
            </td>
          </tr>
          <tr v-if="runs.length === 0">
            <td class="px-6 py-4 border-t text-center text-xl" colspan="6">No runs found.</td>
          </tr>
        </tbody>
      </table>
    </div>
    <!-- Table -->
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import SearchFilter from '@/Shared/SearchFilter'
import SelectInput from '@/Shared/SelectInput'
import DownloadBulkButton from './Partials/DownloadBulkButton'
import DownloadButton from './Partials/DownloadButton'
import Share from '../Projects/Partials/Share'

export default {
  components: {
    Head,
    Icon,
    Link,
    SearchFilter,
    SelectInput,
    DownloadBulkButton,
    DownloadButton,
    Share,
  },
  layout: Layout,
  props: {
    auth       : Object,
    filters    : Object,
    statuses   : Object,
    project    : Object,
    users      : Array,
    company    : Object,
    permissions: Object,
  },
  data() {
    return {
      runs    : this.project.runs,
      selected: [],
      discount: null,
      form: {
        search    : this.filters.search,
        trashed   : this.filters.trashed,
        userSearch: this.filters.userSearch,
      },
    }
  },
  computed: {
    isSelected() {
      return this.selected.length > 0
    },
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get(`/projects/${this.project.id}/runs`, pickBy(this.form), { preserveState: true })
      }, 700),
    },
    project () {
      this.runs = this.project.runs
      this.selected = []
    },
  },
  methods: {
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    toggle() {
      if (this.selected.length > 0) {
        this.selected = []
      } else {
        this.selected = []
        this.selected = this.runs.map(el => el.id)
      }
    },
    clone(id) {
      if (confirm('Are you sure you want to duplicate this run?')) {
        this.$inertia.put(`/runs/${id}/duplicate`, {
          onSuccess: () => {},
        })
      }
    },
    destroy(id) {
      if (confirm('Are you sure you want to delete this run?')) {
        this.$inertia.delete(`/runs/${id}`, {
          onSuccess: () => {},
        })
      }
    },
    restore(id) {
      if (confirm('Are you sure you want to restore this run?')) {
        this.$inertia.put(`/runs/${id}/restore`, {
          onSuccess: () => {},
        })
      }
    },
    updateStatus (status) {
      if (confirm('Are you sure you want to update the project status to ' + this.statuses[status]  +' ?')) {
        this.$inertia.put(`/projects/${this.project.id}/status/${status}`)
      }
    },
    bulkDiscount() {
      this.$inertia.put(`/projects/bulk/${this.project.id}/discount`, {
        runs    : this.selected,
        discount: this.discount,
      },{
        onSuccess: () => {},
      })
    },
    bulkClone() {
      if (confirm('Are you sure you want to duplicate all selected runs?')) {
        this.$inertia.put(`/projects/bulk/${this.project.id}/duplicate`, { runs: this.selected },{
          onSuccess: () => {},
        })
      }
    },
    bulkDestroy() {
      if (confirm('Are you sure you want to delete all selected runs?')) {
        this.$inertia.put(`/projects/bulk/${this.project.id}/destroy`, { runs: this.selected },{
          onSuccess: () => {},
        })
      }
    },
    bulkRestore() {
      if (confirm('Are you sure you want to restore all selected runs?')) {
        this.$inertia.put(`/projects/bulk/${this.project.id}/restore`, { runs: this.selected },{
          onSuccess: () => {},
        })
      }
    },
  },
}
</script>
